import { useState } from 'react';
import {
  Card, Group, Text, Button, Stack, JsonInput, Select, NumberInput,
  Badge, Code, Box, SimpleGrid, Divider, Loader,
} from '@mantine/core';
import { IconBolt, IconPlayerPlay, IconRoute } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useArchbaseRemoteServiceApi } from '@archbase/data';
import { RouteXOptimizationService } from '@services/OptimizationService';
import { API_TYPE } from '@ioc/IOCTypes';
import type { OptimizeResponse, Statistics } from '@domain/routeJob/RouteJobDto';

const EXAMPLE_REQUEST = JSON.stringify({
  vehicles: [
    {
      id: "truck-1",
      startLocation: { lat: -22.9068, lng: -43.1729 },
      endLocation: { lat: -22.9068, lng: -43.1729 },
      compartments: [
        { id: "dry", type: "DRY", capacity: { weight: 3000, volume: 15 } },
        { id: "ref", type: "REFRIGERATED", capacity: { weight: 2000, volume: 10 } }
      ],
      totalCapacity: { weight: 5000, volume: 25 },
      skills: ["multi-temp"],
      shiftStart: "08:00", shiftEnd: "18:00", maxStops: 30
    },
    {
      id: "van-1",
      startLocation: { lat: -22.9068, lng: -43.1729 },
      totalCapacity: { weight: 2000, volume: 10 },
      shiftStart: "08:00", shiftEnd: "14:00", maxStops: 15
    }
  ],
  visits: [
    { id: "v1", location: { lat: -22.911, lng: -43.165 }, demand: { weight: 150, volume: 0.5 }, requiredCompartment: "REFRIGERATED", timeWindow: { start: "08:00", end: "12:00", type: "HARD" }, serviceDuration: 15, priority: 1 },
    { id: "v2", location: { lat: -22.920, lng: -43.170 }, demand: { weight: 500, volume: 2.0 }, requiredCompartment: "DRY", serviceDuration: 10, priority: 2 },
    { id: "v3", location: { lat: -22.915, lng: -43.180 }, demand: { weight: 300, volume: 1.5 }, serviceDuration: 10, priority: 3 },
    { id: "v4", location: { lat: -22.905, lng: -43.175 }, demand: { weight: 200, volume: 1.0 }, requiredCompartment: "REFRIGERATED", serviceDuration: 15, priority: 1 },
    { id: "v5", location: { lat: -22.925, lng: -43.160 }, demand: { weight: 400, volume: 2.0 }, serviceDuration: 10, priority: 4 }
  ]
}, null, 2);

export default function OptimizationPlaygroundView() {
  const [requestJson, setRequestJson] = useState(EXAMPLE_REQUEST);
  const [solverMode, setSolverMode] = useState<string>('BALANCED');
  const [maxTime, setMaxTime] = useState<number>(10);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<OptimizeResponse | null>(null);

  const optimizationService = useArchbaseRemoteServiceApi<RouteXOptimizationService>(API_TYPE.RouteXOptimization);

  const handleOptimize = async () => {
    try {
      const parsed = JSON.parse(requestJson);
      parsed.options = { solverMode, maxSolvingTime: maxTime, objective: 'MINIMIZE_TOTAL_TIME' };
      setLoading(true);
      setResult(null);
      const response = await optimizationService.optimizeSync(parsed);
      setResult(response);
    } catch (e: any) {
      notifications.show({ title: 'Erro', message: e.message, color: 'red' });
    } finally {
      setLoading(false);
    }
  };

  const stats = result?.statistics;

  return (
    <Box p="xl" style={{ height: '100%' }}>
      <Stack gap="xl">
        <Group justify="space-between">
          <Stack gap={2}>
            <Text size="xl" fw={700} style={{ letterSpacing: '-0.02em' }}>Playground</Text>
            <Text size="sm" c="dimmed">Testar otimização de rotas interativamente</Text>
          </Stack>
        </Group>

        <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="lg">
        {/* Input */}
        <Stack gap="md">
          <Card padding="lg" radius="md" withBorder>
            <Text fw={600} size="sm" mb="md">Request</Text>
            <JsonInput
              value={requestJson}
              onChange={setRequestJson}
              minRows={18}
              maxRows={30}
              autosize
              formatOnBlur
              validationError="JSON inválido"
              style={{ fontFamily: 'monospace' }}
            />
          </Card>

          <Card padding="lg" radius="md" withBorder>
            <Group grow mb="md">
              <Select label="Solver Mode" data={[
                { value: 'FAST', label: 'FAST (~100ms)' },
                { value: 'BALANCED', label: 'BALANCED (~5s)' },
                { value: 'FULL', label: 'FULL (~30s)' },
              ]} value={solverMode} onChange={(v) => setSolverMode(v ?? 'BALANCED')} />
              <NumberInput label="Max Tempo (s)" value={maxTime} onChange={(v) => setMaxTime(Number(v))} min={1} max={120} />
            </Group>
            <Button fullWidth size="md" leftSection={loading ? <Loader size={16} color="white" /> : <IconPlayerPlay size={18} />} onClick={handleOptimize} disabled={loading} color="blue">
              {loading ? 'Otimizando...' : 'Executar Otimização'}
            </Button>
          </Card>
        </Stack>

        {/* Result */}
        <Stack gap="md">
          {result ? (
            <>
              <Card padding="lg" radius="md" withBorder style={{ borderLeft: `3px solid var(--mantine-color-${result.status === 'SOLVED' ? 'teal' : 'red'}-6)` }}>
                <Group justify="space-between" mb="md">
                  <Group gap="xs">
                    <IconBolt size={18} />
                    <Text fw={600} size="sm">Resultado</Text>
                  </Group>
                  <Group gap="xs">
                    <Badge color={result.status === 'SOLVED' ? 'teal' : 'red'}>{result.status}</Badge>
                    <Badge variant="outline">{result.solvingTimeMs}ms</Badge>
                  </Group>
                </Group>

                {stats && (
                  <SimpleGrid cols={3} spacing="sm" mb="md">
                    <Stack gap={0} align="center"><Text size="xs" c="dimmed">Veículos</Text><Text fw={700} ff="monospace">{stats.vehiclesUsed}</Text></Stack>
                    <Stack gap={0} align="center"><Text size="xs" c="dimmed">Visitas</Text><Text fw={700} ff="monospace">{stats.visitsAssigned}/{stats.visitsAssigned + stats.visitsUnassigned}</Text></Stack>
                    <Stack gap={0} align="center"><Text size="xs" c="dimmed">Distância</Text><Text fw={700} ff="monospace">{stats.totalDistance}km</Text></Stack>
                  </SimpleGrid>
                )}

                <Text size="xs" c="dimmed" mb={4}>Score</Text>
                <Code mb="md">{result.score ?? '-'}</Code>

                {result.unassignedVisits && result.unassignedVisits.length > 0 && (
                  <Badge color="orange" variant="light" mb="md">{result.unassignedVisits.length} não alocadas: {result.unassignedVisits.join(', ')}</Badge>
                )}
              </Card>

              {result.routes?.map((route, i) => (
                <Card key={i} padding="md" radius="md" withBorder>
                  <Group justify="space-between" mb="sm">
                    <Group gap="xs">
                      <IconRoute size={16} />
                      <Text fw={600} size="sm">{route.vehicleId}</Text>
                    </Group>
                    <Group gap="xs">
                      <Badge size="xs" variant="light">{route.stops.length} paradas</Badge>
                      <Badge size="xs" variant="outline">{route.totalDistance}km</Badge>
                      <Badge size="xs" variant="outline">{route.totalTime}min</Badge>
                    </Group>
                  </Group>
                  <Stack gap={4}>
                    {route.stops.map((stop) => (
                      <Group key={stop.visitId} gap="xs" pl="sm" style={{ borderLeft: '2px solid var(--mantine-color-blue-3)' }}>
                        <Badge size="xs" circle variant="filled">{stop.sequence}</Badge>
                        <Text size="xs" fw={500}>{stop.visitId}</Text>
                        {stop.arrivalTime && <Text size="xs" c="dimmed">{stop.arrivalTime}</Text>}
                        {stop.distanceFromPrevious > 0 && <Text size="xs" c="dimmed" ff="monospace">+{stop.distanceFromPrevious}km</Text>}
                      </Group>
                    ))}
                  </Stack>
                </Card>
              ))}
            </>
          ) : (
            <Card padding="xl" radius="md" withBorder>
              <Stack align="center" py="xl">
                <IconRoute size={48} color="var(--mantine-color-dimmed)" stroke={1} />
                <Text c="dimmed" ta="center">Execute uma otimização para ver os resultados aqui</Text>
              </Stack>
            </Card>
          )}
        </Stack>
        </SimpleGrid>
      </Stack>
    </Box>
  );
}

